// Per-symbol factor patches applied after computeFactors, before scoring.
//
// Use case: a stock's accounting structure makes certain factor inputs broken
// (e.g., BRK's GAAP EPS swings with mark-to-market on its $300B equity book,
// poisoning forward-PE / EPS-CAGR / PEG / EV-EBITDA). Set those factors to null
// here and they fall to the peer-group median (z=0) at scoring time instead of
// dragging the stock's pillar scores.
//
// The JSON file is imported statically (resolveJsonModule) instead of read via
// fs at runtime — fs would force the Node built-in into the client bundle
// because this module is statically reachable from `lib/scoring.ts` and the
// scoring chain is imported by client components even though scoreStocks itself
// is only called server-side. A static import inlines the data into both
// bundles cleanly.

import overridesJson from '../data/factor-overrides.json';
import type { StockFactors } from './types';

export type FactorPatch = Partial<Record<keyof StockFactors, number | null>>;
export type FactorOverridesMap = Record<string, FactorPatch>;

let cached: FactorOverridesMap | null = null;

function buildMap(source: Record<string, Record<string, unknown>>): FactorOverridesMap {
  // Strip doc-only keys (anything starting with _) at both the symbol level
  // (e.g., top-level `_doc` description) and the patch level (e.g., per-symbol
  // `_note`). These are conventionally JSON-comment substitutes — they must
  // not flow into the factors object via the spread in applyFactorOverrides.
  const out: FactorOverridesMap = {};
  for (const [sym, patch] of Object.entries(source)) {
    if (sym.startsWith('_')) continue;
    const cleanPatch: FactorPatch = {};
    for (const [k, v] of Object.entries(patch)) {
      if (k.startsWith('_')) continue;
      cleanPatch[k as keyof StockFactors] = v as number | null;
    }
    out[sym] = cleanPatch;
  }
  return out;
}

export function loadFactorOverrides(): FactorOverridesMap {
  if (cached !== null) return cached;
  cached = buildMap(overridesJson as Record<string, Record<string, unknown>>);
  return cached;
}

export function applyFactorOverrides<T extends StockFactors>(symbol: string, factors: T): T {
  const overrides = loadFactorOverrides();
  const patch = overrides[symbol];
  if (!patch) return factors;
  return { ...factors, ...patch } as T;
}

// Test-only helper to bust the module-level cache between cases.
export function _resetFactorOverridesCache(): void {
  cached = null;
}

// Test-only helper to inject a synthetic overrides map without touching disk.
export function _setFactorOverridesForTest(map: FactorOverridesMap): void {
  cached = map;
}
